import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { Grid, Box } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import {
  PermissionsGate,
  UploadSellRequestDocument,
  SellRequestsForm,
  MoreButton,
  TabsV2,
  CommentsV2,
  ActivityListV2,
  TaskCarDetails,
  TaskDetailScrollableSection,
  TaskSection,
} from '@components';
import { UploadDocumentV3 } from 'components/common/media';
import { ButtonV2 } from 'components/common/ui';
import { DrawerLayout, TaskLayout } from '@layouts';

import { useSellRequestsTaskOperations, usePermissions } from '@hooks';

import { asyncForEach, showError, showSuccess } from '@utils';
import { boardsServices } from '@services';
import {
  SELL_REQUESTS_TASK_FIELD_NAMES,
  SELL_REQUESTS_TASK_STATUSES_NAMES,
  SELL_REQUESTS_STATUSES_NAMES_MAP,
  SELL_VEHICLES_ACTION,
} from 'components/sellRequests/constants';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { sellRequestsPayload } from 'helpers';

export const tabs = [
  {
    label: 'Information',
    id: 'ticket-info',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Buyout Details',
    id: 'buyout-details',
    icon: <BuildIcon data-testid="BuildIcon" />,
  },
  {
    label: 'Loans',
    id: 'loans',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'Deposit',
    id: 'deposit',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'For Sale',
    id: 'for-sale',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'For Transfer',
    id: 'for-transfer',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    icon: <FormatListBulletedIcon data-testid="FormatListBulletedIcon" />,
    id: 'ticket-details-right',
    label: 'Ticket Details',
  },
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
    label: 'Car Details',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
    label: 'Documents',
  },
];

const taskStatusNames = Object.keys(SELL_REQUESTS_TASK_STATUSES_NAMES).reduce(
  (acc, cur) => {
    acc[cur] = SELL_REQUESTS_TASK_STATUSES_NAMES[cur].label;

    return acc;
  },
  {}
);

const SellRequestsTask = () => {
  const {
    task,
    vehicle,
    taskId,
    mentionedUsers,
    assignees,
    loading,
    stages,
    updateTask,
    getSellRequestsTask,
  } = useSellRequestsTaskOperations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assigneesLoanOptions, setAssigneesLoanOptions] = useState([]);
  const { findEntity } = usePermissions();

  const submitBtnRef = useRef();

  const handleChangeCarDetails = async ({ field, value }) => {
    const payload = {
      [field]: value,
    };

    try {
      setIsSubmitting(true);
      await boardsServices.updateTask(taskId, payload);

      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we save the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.addTaskComment(taskId, comment);

      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      await submitBtnRef?.current?.click?.();
      await boardsServices.editTaskComment(taskId, commentId, comment);
      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      await submitBtnRef?.current?.click?.();
      await boardsServices.deleteTaskComment(taskId, commentId);
      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadDocument = async (form) => {
    try {
      setIsSubmitting(true);
      await asyncForEach(form?.documents, async (document) => {
        await boardsServices.uploadTaskDocument(taskId, document, form?.type);
      });
      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskDocument(taskId, docId);
      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const makeAction = async (actionId) => {
    try {
      await boardsServices.makeAction(taskId, actionId);
      showSuccess('The offer is send');
    } catch (error) {
      showError(error);
    }
  };

  const handleSendOffer = async () => {
    try {
      await boardsServices.sendOffer(taskId);
      showSuccess('The offer is send');
    } catch (error) {
      showError(error);
    }
  };

  const handleChangeStatus = async (status) => {
    try {
      await updateTask({
        id: taskId,
        status,
      });
      getSellRequestsTask();
    } catch (error) {
      showError(error);
    }
  };

  const checkForSaleChecklist = () => {
    if (task?.customFields?.forSaleChecklist) {
      return !(
        task.customFields.forSaleChecklist.kteo &&
        task.customFields.forSaleChecklist.purchaseInvoice &&
        task.customFields.forSaleChecklist.invoice &&
        task.customFields.forSaleChecklist.vehicleTransferAuthorization &&
        task.customFields.forSaleChecklist.certificateOfUnpaidTrafficFees
      );
    }
    return true;
  };

  const shouldShowDepositButton = () => {
    if (
      task?.status?.value === SELL_REQUESTS_STATUSES_NAMES_MAP.Loans &&
      task?.customFields?.loanRequestStatus === 'Approved'
    ) {
      return false;
    }
    return true;
  };

  const getAgents = async () => {
    const validAgents = assignees?.filter(
      (agent) =>
        agent?.role === 'CreditControlLead' ||
        agent?.role === 'CreditControlAssociate'
    );
    setAssigneesLoanOptions(validAgents);
  };

  const handleSubmit = async (form) => {
    try {
      setIsSubmitting(true);

      const values = sellRequestsPayload(form, task, taskId);

      await updateTask(values);
      await getSellRequestsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (task?.id) {
      getAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  const renderHeaderOptions = () => {
    return (
      <PermissionsGate entity={ENTITIES_PERMISIONS.WriteSaleRequests}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MoreButton
            moreOptions={[
              {
                title: 'Send Offer',
                onClick: () => handleSendOffer(),
                hidden: !findEntity([ENTITIES_PERMISIONS.WriteSaleRequests]),
              },
              {
                title: 'Send Loan Offer',
                onClick: () =>
                  makeAction(SELL_VEHICLES_ACTION.sendVehicleSalesOfferEmail),
                hidden: !findEntity([ENTITIES_PERMISIONS.WriteSaleRequests]),
              },
              {
                title: 'Move to Deposit',
                onClick: () =>
                  handleChangeStatus(
                    SELL_REQUESTS_STATUSES_NAMES_MAP.BuyoutDeposit
                  ),
                hidden:
                  shouldShowDepositButton() ||
                  !findEntity([ENTITIES_PERMISIONS.WriteSaleRequests]),
              },
              {
                title: 'Move to transferred',

                onClick: () =>
                  handleChangeStatus(
                    SELL_REQUESTS_STATUSES_NAMES_MAP.Transferred
                  ),
                hidden:
                  !findEntity([ENTITIES_PERMISIONS.WriteSaleRequests]) ||
                  task?.status?.value ===
                    SELL_REQUESTS_STATUSES_NAMES_MAP.VehicleSales
                    ? checkForSaleChecklist()
                    : true,
              },
              {
                title: 'Rejected',
                onClick: () =>
                  handleChangeStatus(
                    SELL_REQUESTS_STATUSES_NAMES_MAP.CustomerRejected
                  ),
                hidden: !findEntity([ENTITIES_PERMISIONS.WriteSaleRequests]),
              },
            ]}
          />
          <ButtonV2
            ref={submitBtnRef}
            type="submit"
            size="small"
            form="sell-requests-form"
            disabled={loading || isSubmitting}
          >
            Save
          </ButtonV2>
        </Box>
      </PermissionsGate>
    );
  };

  return (
    <>
      <Head>
        <title>{`Sell Request: ${taskId} `}</title>
        <meta name="description" content="instacar instafleet" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={taskId}
        onReload={getSellRequestsTask}
        renderHeaderOptions={renderHeaderOptions}
        entity={ENTITIES_PERMISIONS.ReadSaleRequests}
        stages={stages}
        currentStage={task?.status?.value}
        onChangeStage={handleChangeStatus}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="sellRequest-task-details" />

            <TaskDetailScrollableSection id="sellRequest-task-details">
              <SellRequestsForm
                defaultValues={task}
                onSubmit={handleSubmit}
                loanAssignees={assigneesLoanOptions}
                assignees={assignees}
              />
              <TaskSection
                title={tabs[6].label}
                id={tabs[6].id}
                icon={tabs[6].icon}
              >
                <CommentsV2
                  comments={task?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={mentionedUsers}
                />
              </TaskSection>
              <TaskSection
                title={tabs[7].label}
                id={tabs[7].id}
                icon={tabs[7].icon}
              >
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={SELL_REQUESTS_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                  assignees={assignees}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2 tabs={rightTabs} observerRootId="sellRequest-task-right" />
            <TaskDetailScrollableSection id="sellRequest-task-right">
              <TaskSection
                isSmall
                title={rightTabs[0].label}
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TaskCarDetails
                  task={task}
                  assignees={assignees}
                  stages={stages}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TaskCarDetails
                  vehicle={vehicle}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[2].label}
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadDocumentV3
                  enableUpload={false}
                  documents={task?.documents}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || isSubmitting}
                  UploadComponent={
                    <UploadSellRequestDocument
                      onSubmit={handleUploadDocument}
                      isLoading={loading || isSubmitting}
                    />
                  }
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

SellRequestsTask.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default SellRequestsTask;
